import { useReactiveVar } from '@apollo/client/react';
import { Grid, List, Stack, Typography } from '@mui/material';
import { UserIdentityProvider } from 'graphql/generated/graphqlTypes';
import useAccess from 'hooks/allVisitors/pages/userDetails/useAccess';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useState } from 'react';
import { authVar } from 'state/vars';
import QRCodeValueUtil from 'utils/QRCodeValue/QRCodeValue.util';
import { isVisitorHasInstallerRole } from 'utils/Visitor/visitor';
import Loader from 'v2/components/Loader/Loader';
import ModalTemplate from 'v2/components/Modal/ModalTemplate';
import useModalContext from 'v2/components/Modal/useModalContext';
import SectionPaper from 'v2/components/SectionPaper/SectionPaper';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useExternalUsers from 'v2/hooks/useExternalUsers/useExternalUsers';
import { formatTranslation } from 'v2/utils/formatTranslation';
import { getVisitorOrigin, VisitorOrigin } from 'v2/utils/visitorOrigin';
import BlockCardModal from './BlockCardModal';
import MobileAppRowConnected from './MobileAppRowConnected';
import MobileAppRowDisconnected from './MobileAppRowDisconnected';
import NfcCardRow from './NfcCardRow';

interface Props {
  visitorId: string;
}

const AccessSection: React.FC<Props> = ({ visitorId }) => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const { user } = useReactiveVar(authVar);
  const handleFetchError = (errorMessage: string) => showSnackbar(errorMessage, { severity: 'error' });
  const {
    visitorDetails,
    loadingVisitorDetails,
    isWaitingForConfigDevice,
    awaitingDisconnectDeviceId,
    startAwaitingDisconnect,
    mobileDeviceConfigApp,
    mobileDeviceAccessApp,
    visitorCards,
    loadingVisitorCards,
    blockNfcCard,
    blockNfcCardLoading
  } = useAccess({
    translation,
    handleFetchError,
    visitorId
  });
  const {
    sendInvitationForMobileDeviceById,
    sendInvitationForMobileDeviceByIdMutationLoading,
    unregisterMobileDeviceById,
    unregisterMobileDeviceByIdMutationLoading
  } = useExternalUsers({
    translation,
    handleFetchError
  });
  const { openModal, closeModal } = useModalContext();
  // sendInvitationForMobileDeviceByIdMutationLoading is shared between the access-app and config-app rows,
  // so this tracks which device triggered the mutation and lets only that row show the loading state.
  const [resendingDeviceId, setResendingDeviceId] = useState<string | null>(null);

  const isOwnProfile = visitorId === user?.id;

  const accessQrCodeValue =
    isOwnProfile && mobileDeviceAccessApp?.registrationToken
      ? QRCodeValueUtil.getQrCodeValue(mobileDeviceAccessApp.registrationToken)
      : undefined;

  const visitorOrigin = visitorDetails ? getVisitorOrigin(visitorDetails) : undefined;

  const showConfigAppStatus = mobileDeviceConfigApp && visitorDetails && isVisitorHasInstallerRole(visitorDetails);

  // Cognito and Entra ID users have access to the web portal, so they don't need the resend button
  // to get the QR code by email to connect their device — they can get it themselves in the portal.
  // Key sharing users don't need an email either, since they don't connect a device at all — they
  // get a QR code to scan at the entrance instead.
  const isResendButtonVisible =
    visitorOrigin !== undefined &&
    visitorOrigin !== VisitorOrigin.KeySharing &&
    visitorOrigin !== VisitorOrigin.EntraId &&
    visitorDetails?.identityProvider !== UserIdentityProvider.Cognito;

  const openBlockCardModal = (cardUuid: string) => {
    openModal(
      <BlockCardModal cardUuid={cardUuid} blockNfcCard={blockNfcCard} blockNfcCardLoading={blockNfcCardLoading} />
    );
  };

  const handleResendInvitationForMobileDevice = async (mobileDeviceId: string) => {
    if (!sendInvitationForMobileDeviceByIdMutationLoading) {
      setResendingDeviceId(mobileDeviceId);
      try {
        const result = await sendInvitationForMobileDeviceById(visitorId, mobileDeviceId);
        if (result?.sendInvitationForMobileDeviceById && visitorDetails?.email)
          showSnackbar(formatTranslation(translation.email_sent_to_notice, { email: visitorDetails.email }), {
            severity: 'success'
          });
      } finally {
        setResendingDeviceId(null);
      }
    }
  };

  const handleDisconnectMobileDevice = async (mobileDeviceId: string) => {
    if (!unregisterMobileDeviceByIdMutationLoading) {
      const result = await unregisterMobileDeviceById(visitorId, mobileDeviceId);
      if (result?.unregisterMobileDeviceById) {
        showSnackbar(translation.app_disconnected, { severity: 'success' });
        startAwaitingDisconnect(mobileDeviceId);
        closeModal();
      }
    }
  };

  const openDisconnectMobileDeviceModal = (mobileDeviceId: string) => {
    openModal(
      <ModalTemplate
        title={translation.disconnect_device}
        primaryAction={{
          label: translation.confirm,
          onClick: () => handleDisconnectMobileDevice(mobileDeviceId),
          dangerzone: true,
          id: 'disconnect-device-confirm-button'
        }}
        secondaryAction={{ label: translation.cancel, id: 'disconnect-device-cancel-button' }}
      >
        <Typography>{translation.disconnect_device_description}</Typography>
      </ModalTemplate>
    );
  };

  const isLoading = loadingVisitorDetails || loadingVisitorCards;

  if (isLoading || !visitorDetails) {
    return (
      <SectionPaper>
        <Loader />
      </SectionPaper>
    );
  }

  return (
    <SectionPaper>
      <Grid container direction="column" spacing={2}>
        <Typography variant="h5">{translation.access}</Typography>
        <List component="nav">
          <Grid container spacing={0.5}>
            {mobileDeviceAccessApp && (
              <Grid size={12}>
                {mobileDeviceAccessApp.isRegistered ? (
                  <MobileAppRowConnected
                    idPrefix="access-app"
                    mobileDeviceId={mobileDeviceAccessApp.id}
                    label={translation.atlas_access_app}
                    onDisconnect={openDisconnectMobileDeviceModal}
                    disconnectLoading={
                      unregisterMobileDeviceByIdMutationLoading ||
                      awaitingDisconnectDeviceId === mobileDeviceAccessApp.id
                    }
                  />
                ) : (
                  <MobileAppRowDisconnected
                    idPrefix="access-app"
                    mobileDeviceId={mobileDeviceAccessApp.id}
                    label={translation.atlas_access_app}
                    isResendButtonVisible={isResendButtonVisible}
                    sendInvitationLoading={
                      sendInvitationForMobileDeviceByIdMutationLoading && resendingDeviceId === mobileDeviceAccessApp.id
                    }
                    resendDisabled={sendInvitationForMobileDeviceByIdMutationLoading}
                    onResendInvitation={handleResendInvitationForMobileDevice}
                    qrCodeValue={accessQrCodeValue}
                  />
                )}
              </Grid>
            )}
            {isWaitingForConfigDevice && (
              <Grid size={12}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ padding: 1 }}>
                  <Loader size={1} />
                  <Typography variant="caption" color="text.secondary">
                    {translation.refreshing_data}
                  </Typography>
                </Stack>
              </Grid>
            )}
            {showConfigAppStatus && mobileDeviceConfigApp && (
              <Grid size={12}>
                {mobileDeviceConfigApp.isRegistered ? (
                  <MobileAppRowConnected
                    idPrefix="config-app"
                    mobileDeviceId={mobileDeviceConfigApp.id}
                    label={translation.atlas_config_app}
                    onDisconnect={openDisconnectMobileDeviceModal}
                    disconnectLoading={
                      unregisterMobileDeviceByIdMutationLoading ||
                      awaitingDisconnectDeviceId === mobileDeviceConfigApp.id
                    }
                  />
                ) : (
                  <MobileAppRowDisconnected
                    idPrefix="config-app"
                    mobileDeviceId={mobileDeviceConfigApp.id}
                    label={translation.atlas_config_app}
                    isResendButtonVisible
                    sendInvitationLoading={
                      sendInvitationForMobileDeviceByIdMutationLoading && resendingDeviceId === mobileDeviceConfigApp.id
                    }
                    resendDisabled={sendInvitationForMobileDeviceByIdMutationLoading}
                    onResendInvitation={handleResendInvitationForMobileDevice}
                  />
                )}
              </Grid>
            )}
            {visitorCards.map((card, index) => (
              <Grid key={card.uuid} size={12}>
                <NfcCardRow index={index} card={card} translation={translation} onBlockCard={openBlockCardModal} />
              </Grid>
            ))}
          </Grid>
        </List>
      </Grid>
    </SectionPaper>
  );
};

export default AccessSection;
